import { AnimatePresence, m } from "framer-motion";
import {
	BookOpen,
	Hammer,
	Info,
	LayoutDashboard,
	LogIn,
	LogOut,
	Mail,
	X,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useIsMobile } from "../hooks/useIsMobile";
import { useT } from "../i18n";
import "./MobileNavDrawer.css";

interface MobileNavDrawerProps {
	open: boolean;
	onClose: () => void;
	user: { id?: string } | null;
	onSignOut?: () => void | Promise<void>;
}

export function MobileNavDrawer({
	open,
	onClose,
	user,
	onSignOut,
}: MobileNavDrawerProps) {
	const t = useT();
	const isMobile = useIsMobile();
	const previousFocus = useRef<HTMLElement | null>(null);
	const panelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (open && !isMobile) onClose();
	}, [isMobile, onClose, open]);

	// ESC closes; lock body scroll while open; restore focus on close.
	useEffect(() => {
		if (!open || !isMobile) return;
		previousFocus.current = (document.activeElement as HTMLElement) ?? null;
		const handler = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handler);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		// Move focus into the panel for keyboard users.
		panelRef.current?.focus();
		return () => {
			document.removeEventListener("keydown", handler);
			document.body.style.overflow = previousOverflow;
			previousFocus.current?.focus?.();
		};
	}, [isMobile, open, onClose]);

	return (
		<AnimatePresence>
			{open && isMobile && (
				<>
					<m.div
						className="mobile-nav-drawer__backdrop"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.18 }}
						onClick={onClose}
					/>
					<m.div
						ref={panelRef}
						id="mobile-nav-drawer"
						className="mobile-nav-drawer__panel"
						role="dialog"
						aria-modal="true"
						aria-label={t.toolbar_menu}
						tabIndex={-1}
						initial={{ x: "100%" }}
						animate={{ x: 0 }}
						exit={{ x: "100%" }}
						transition={{ type: "spring", damping: 30, stiffness: 320 }}
					>
						<div className="mobile-nav-drawer__header">
							<span className="mobile-nav-drawer__title">{t.toolbar_menu}</span>
							<button
								type="button"
								className="mobile-nav-drawer__close"
								onClick={onClose}
								aria-label={t.modal_close}
							>
								<X size={20} strokeWidth={2} />
							</button>
						</div>

						<nav
							className="mobile-nav-drawer__list"
							aria-label={t.toolbar_menu}
						>
							<Link
								to="/wizard"
								className="mobile-nav-drawer__cta"
								onClick={onClose}
							>
								<Hammer size={18} strokeWidth={1.8} aria-hidden="true" />
								{t.landing_primaryCta}
							</Link>
							<Link
								to="/guide/bygga-bastu"
								className="mobile-nav-drawer__link"
								onClick={onClose}
							>
								<BookOpen size={18} strokeWidth={1.8} aria-hidden="true" />
								{t.footer_buildGuide}
							</Link>
							<Link
								to="/about"
								className="mobile-nav-drawer__link"
								onClick={onClose}
							>
								<Info size={18} strokeWidth={1.8} aria-hidden="true" />
								{t.footer_about}
							</Link>
							<Link
								to="/contact"
								className="mobile-nav-drawer__link"
								onClick={onClose}
							>
								<Mail size={18} strokeWidth={1.8} aria-hidden="true" />
								{t.footer_contact}
							</Link>
							{user && (
								<Link
									to="/dashboard"
									className="mobile-nav-drawer__link"
									onClick={onClose}
								>
									<LayoutDashboard
										size={18}
										strokeWidth={1.8}
										aria-hidden="true"
									/>
									{t.nav_dashboard}
								</Link>
							)}
						</nav>

						<div className="mobile-nav-drawer__footer">
							{user ? (
								<button
									type="button"
									className="mobile-nav-drawer__cta mobile-nav-drawer__cta--ghost"
									onClick={() => {
										onClose();
										onSignOut?.();
									}}
								>
									<LogOut size={16} strokeWidth={2} />
									{t.nav_signOut}
								</button>
							) : (
								<Link
									to="/auth"
									className="mobile-nav-drawer__cta mobile-nav-drawer__cta--ghost"
									onClick={onClose}
								>
									<LogIn size={16} strokeWidth={2} />
									{t.nav_signIn}
								</Link>
							)}
						</div>
					</m.div>
				</>
			)}
		</AnimatePresence>
	);
}
